import { cloneDeep, isUndefined } from 'lodash';
import { defaultModifierControls, flattenSteps } from './utils';
import generateId from 'src/lib/generateId';
import SidekickThread, { ModifierValue, NextData } from 'src/models/Sidekick/SidekickThreads';
import { ModifierSpec } from 'src/models/Sidekick/SidekickModuleSpec';

type ModuleRelationship = { id: number; thread: SidekickThread; modifierId: string };

export const addModifier = (modifierIndex, module: NextData, modifierSpec: ModifierSpec, options: { appFeatures }) => {
  const newValue = {
    name: modifierSpec.name,
    controlValues: {},
    integrations: {},
  } as ModifierValue;
  const modifierValue = cloneDeep(defaultModifierControls(modifierSpec, newValue)) as ModifierValue;
  modifierValue.id = generateId();

  if (newValue.name === 'addTopics' && !!options.appFeatures?.sidekickThreadUseTopicIds) {
    // for add topics sub-action, if the feature flag is enabled, set the useTopicIds to true
    modifierValue.controlValues.useTopicIds = true;
  }

  if (modifierValue.name === 'rule') {
    modifierValue.controlValues.rule.then = { anonymousModule: {} };
  }

  if (module.modifierValues === null) {
    module.modifierValues = [];
  }

  if (isUndefined(modifierIndex)) {
    module.modifierValues.push(modifierValue);
    return;
  }

  const aboveModuleIndex = modifierIndex
    ? module.modifierValues.findIndex((data: unknown) => (data as { id: string }).id === modifierIndex) + 1
    : 0;

  module.modifierValues.splice(aboveModuleIndex, 0, modifierValue);
};

export const getThreadsHierarchy = (threads: SidekickThread[]): Record<string, string[]> => {
  const threadsHierarchy: Record<string, string[]> = {};
  threads.forEach(thread => {
    const steps = flattenSteps(thread.nextData);

    for (const mod of steps) {
      if (mod.mod.name !== 'rule') {
        continue;
      }
      if (!mod.mod.controlValues.rule.then.pivotFlowID) {
        continue;
      }

      if (!threadsHierarchy[thread.id]) {
        threadsHierarchy[thread.id] = [];
      }
      if (!threadsHierarchy[thread.id].includes(mod.mod.controlValues.rule.then.pivotFlowID)) {
        threadsHierarchy[thread.id].push(mod.mod.controlValues.rule.then.pivotFlowID);
      }
    }
  });
  return threadsHierarchy;
};

export const getThreadModulesHierarchy = (currentThread: SidekickThread, threads: SidekickThread[]) => {
  const moduleRelations: Record<string, ModuleRelationship[]> = {};

  const threadsObj = threads.reduce((acc, thread) => {
    return { ...acc, [thread.id]: thread };
  }, {});

  if (!currentThread) {
    return moduleRelations;
  }

  currentThread.nextData.forEach((module: NextData) => {
    const pivotFlows = module.modifierValues
      .filter(modifier => modifier.name === 'rule' && modifier.controlValues?.rule?.then?.pivotFlowID)
      .map(modifier => ({
        id: modifier.controlValues?.rule?.then?.pivotFlowID,
        thread: threadsObj[modifier.controlValues?.rule?.then?.pivotFlowID],
        modifierId: modifier.id,
      }));

    moduleRelations[module.id!] = pivotFlows as ModuleRelationship[];
  });

  return moduleRelations;
};

export const getThreadsParents = (threads: SidekickThread[]) => {
  return threads.reduce((acc, thread) => {
    const threadsHierarchy = getThreadModulesHierarchy(thread, threads);

    const parentIds = Object.keys(threadsHierarchy)
      .filter(parentId => !!threadsHierarchy[parentId])
      .map(parentId => threadsHierarchy[parentId].map(child => child.id))
      .flat();

    return {
      ...acc,
      [thread.id]: threads.filter(thread => parentIds.includes(Number(thread.id))),
    };
  }, {});
};

export const sendCardAction = {
  id: 'sendCard',
  externalAction: null,
  name: 'sendCard',
  presentableName: 'Send Card',
  presentableDescription: 'Send a card to the user',
  controlCategories: [
    {
      name: 'Template Selection',
      controlGroups: [
        {
          name: '',
          controls: [
            {
              name: 'namespace',
              type: 'hidden',
              prompt: '',
              defaultValue: null,
            },
            {
              name: 'template',
              type: 'template',
              prompt: 'Choose the template you wish to use for your card!',
              defaultValue: null,
            },
            {
              name: 'templateInput',
              type: 'templateInput',
              prompt: 'Choose the input data for your card.',
              defaultValue: null,
            },
          ],
        },
      ],
    },
  ],
  previews: [],
  modifiers: [],
  integrations: null,
  inMemories: null,
  outMemories: null,
};
